import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { useParams } from 'react-router';
import { Box, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { AxiosResponse } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { useQuery } from 'src/hooks/useQuery';
import * as Yup from 'yup';
import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js';
import { getIdentityLabel } from 'components/settings/model/Settings';
import { PasswordFormField } from 'lib/form/PasswordFormField';
import { Link } from 'lib/Link';
import { useLocalHistory } from 'lib/useLocalHistory';
import { BaseRouteParams, ROUTES } from 'pages/routes';
import { signIn } from 'store/auth/authSlice';
import { resetRewards } from 'store/basket/basketSlice';
import { ApplicationState } from 'store/store';
import { LoadingButton } from 'ui/LoadingButton';
import { UserData, formatPhoneAsE164, getIdentityValidationScheme } from './model/User';
import { getOnCloseRedirectUrl } from './localAuth';
import { GoogleSSO } from './GoogleSSO';
import { AppleSSO } from './AppleSSO';
import { TermsAndConditions } from './TermsAndCondition';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { CountryCodePicker, getCountryOptions, getDefaultCountry } from './CountryCodePicker';
import { IdentitySelector } from './IdentitySelector';

const PREFIX = 'LoginForm';

const classes = {
    root: `${PREFIX}root`,
    button: `${PREFIX}button`,
    forgot: `${PREFIX}forgot`,
    forgotWith2FA: `${PREFIX}forgotWith2FA`,
    orContainer: `${PREFIX}orContainer`,
    or: `${PREFIX}or`
};

const StyledGrid = styled(Grid, {
    shouldForwardProp: prop => prop !== 'noPadding'
})<{ noPadding?: boolean }>(({ theme, noPadding }) => ({
    padding: noPadding ? 0 : theme.spacing(2),
    [`& .${classes.button}`]: {
        height: '48px',
        fontSize: '1.2em'
    },
    [`& .${classes.forgot}`]: {
        margin: theme.spacing(0, 1.75),
        marginTop: theme.spacing(1),
        display: 'block'
    },
    [`& .${classes.forgotWith2FA}`]: {
        margin: theme.spacing(0, 1.75),
        marginTop: theme.spacing(-1),
        display: 'block'
    },
    [`& .${classes.orContainer}`]: {
        position: 'relative',
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2)
    },
    [`& .${classes.or}`]: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(1, 3),
        color: theme.palette.divider
    }
}));

// Existing Yup.string().or() extension lives in logIn.tsx; not re-registered here to avoid
// double-registration warnings. The page that imports this module is responsible for it.

export interface LoginFormData {
    identity: string;
    email: string;
    phone: string;
    password: string;
}

export type LoginTab = 'phone' | 'email';

export interface LoginFormProps {
    /** Pre-fill the identity field (e.g. email captured upstream by the receipt flow). Used
     *  for the single-input identity field, and for the active tab's field in tab mode. */
    defaultIdentity?: string;
    /** Explicit pre-fill for the phone-tab field in tab mode. Useful when the parent knows
     *  the user's phone separately from their email (e.g. `phoneNumberLastUsedToOrder`),
     *  so both tabs can be pre-filled at once. Ignored when not in tab mode. */
    defaultPhone?: string;
    /**
     * Preselect the identity tab when both email and phone auth are enabled with multiple
     * country codes (the only case where the tab toggle is rendered). Useful when the parent
     * knows which identity type the user provided.
     */
    defaultTab?: LoginTab;
    /**
     * Called after a successful sign-in. When provided, the default behaviour of navigating to
     * the previously-stored return URL is suppressed — the parent decides what to do next
     * (e.g. dismiss an inline form, refetch the current page's data).
     */
    onSuccess?: (res: UserData) => void;
    /** Hide the "No account? Sign up" link (useful when sign-up is reached another way). */
    hideSignUpLink?: boolean;
    /** Hide the trailing Terms & Conditions block. */
    hideTermsAndConditions?: boolean;
    /** Drop the outer Grid padding — useful when the form is embedded in a tight container. */
    noPadding?: boolean;
    /** Lay out the identity input and password field side-by-side on a single row (only
     *  applies in single-input mode — tab mode keeps the full-width layout because the
     *  toggle needs the space). */
    compact?: boolean;
    /** If provided, the "Don't have an account? Sign up" link calls this instead of
     *  navigating to `/register`. Useful when the parent renders the form inline and wants
     *  to swap to a sign-up form in the same surface. */
    onSwitchToSignUp?: () => void;
    /** If provided, the "Forgot password?" link calls this instead of navigating to the
     *  forgot-password page. Useful when the parent renders the form inline and wants to
     *  swap to a forgot-password form in the same surface. */
    onSwitchToForgotPassword?: () => void;
    /** If provided, called with the submitted identity instead of navigating to the 2FA page.
     *  Useful when the parent renders the form inline and wants to swap to a 2FA form. */
    onSwitchToTwoFactor?: (identity: string) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
    defaultIdentity,
    defaultPhone,
    defaultTab = 'phone',
    onSuccess,
    hideSignUpLink = false,
    hideTermsAndConditions = false,
    noPadding = false,
    compact = false,
    onSwitchToSignUp,
    onSwitchToForgotPassword,
    onSwitchToTwoFactor
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { merchantId } = useParams<BaseRouteParams>();
    const { logUserEvent } = useGAHelpers();
    const queryIdentity = useQuery('identity');
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const [errorMessage, setErrorMessage] = React.useState('');
    const { isLoading: isAuthLoading } = useSelector((state: ApplicationState) => state.auth);
    const [selectedCountry, setSelectedCountry] = React.useState<string>(() =>
        getDefaultCountry(
            settings?.phoneAuth?.supportedCountryCallingCodes ?? [],
            settings?.region?.phoneNumberCode
        )
    );
    const [activeTab, setActiveTab] = React.useState<LoginTab>(defaultTab);
    const { push, getParsedPath } = useLocalHistory();

    const identity = React.useMemo(
        () => getIdentityLabel(settings?.emailAuthEnabled, settings?.phoneAuthEnabled),
        [settings?.emailAuthEnabled, settings?.phoneAuthEnabled]
    );
    // Parent decides what `defaultIdentity` to pass based on the form's identity context.
    // Used as-is — no type-based filter here.
    const userIdentity = defaultIdentity ?? queryIdentity ?? undefined;

    const countryOptions = React.useMemo(
        () => getCountryOptions(settings?.phoneAuth?.supportedCountryCallingCodes ?? []),
        [settings?.phoneAuth?.supportedCountryCallingCodes]
    );

    const hasMultipleCountryCodes = countryOptions.length > 1;

    // both enabled + multiple country codes - show tab toggle
    const useSeparateInputs = identity === 'both' && settings?.phoneAuthEnabled && hasMultipleCountryCodes;

    const showCountryInfo = identity === 'phone' || useSeparateInputs;

    // Strip the international prefix from a phone value — the field is rendered alongside a
    // country-code selector so the input shouldn't duplicate it. Falls back to the raw value
    // on parse failure (already-national, or unparseable).
    const normalizePhone = React.useCallback((value: string | undefined) => {
        if (!value) {
            return undefined;
        }
        try {
            const parsed = parsePhoneNumberFromString(value);
            return parsed?.nationalNumber.toString() ?? value;
        } catch {
            return value;
        }
    }, []);

    const normalizedDefaultPhone = React.useMemo(
        () => normalizePhone(defaultPhone),
        [defaultPhone, normalizePhone]
    );

    const initialFields: LoginFormData = React.useMemo(() => {
        // When the email/phone tabs are rendered, the form submits the active tab's field
        // rather than `identity`. Pre-fill both tabs when we can: `defaultIdentity` goes to
        // whichever tab is the active one (per `defaultTab`); `normalizedDefaultPhone` always
        // goes to the phone tab (useful when the parent knows the phone separately).
        // In single-input mode (no tabs), only the `identity` field is used.
        if (!useSeparateInputs) {
            return { identity: userIdentity ?? '', email: '', phone: '', password: '' };
        }
        const emailPrefill = defaultTab === 'email' ? userIdentity ?? '' : '';
        // In tab mode the phone field sits alongside a country picker, so strip the
        // international prefix from defaultIdentity if it's been passed as E.164.
        const phonePrefill =
            defaultTab === 'phone'
                ? normalizePhone(userIdentity) ?? normalizedDefaultPhone ?? ''
                : normalizedDefaultPhone ?? '';
        return { identity: '', email: emailPrefill, phone: phonePrefill, password: '' };
    }, [defaultTab, normalizedDefaultPhone, useSeparateInputs, userIdentity, normalizePhone]);

    const has2FA = React.useMemo(
        () => !!settings && !!settings.iam && settings.iam.appUserAuth === 'VERIFY_PASSWORD',
        [settings]
    );

    const handleCountryChange = React.useCallback(
        (setFieldValue: FormikHelpers<LoginFormData>['setFieldValue']) => (value: string) => {
            setSelectedCountry(value);
            if (useSeparateInputs) {
                setFieldValue('phone', '');
            } else {
                setFieldValue('identity', '');
            }
        },
        [useSeparateInputs]
    );
    const handleTabChange = React.useCallback((newTab: LoginTab) => {
        setActiveTab(newTab);
        // Intentionally don't clear `email`/`phone` here — `handleSubmit` already picks the
        // right field based on `activeTab`, and clearing wipes any pre-fill the parent has
        // supplied (e.g. `defaultPhone` from `phoneNumberLastUsedToOrder`) the first time
        // the user clicks the phone tab.
    }, []);
    const signinValidation = React.useMemo(() => {
        if (useSeparateInputs) {
            return Yup.object().shape({
                ...(activeTab === 'email' && {
                    email: getIdentityValidationScheme('email', selectedCountry as CountryCode, t)
                }),
                ...(activeTab === 'phone' && {
                    phone: getIdentityValidationScheme('phone', selectedCountry as CountryCode, t)
                }),
                ...(!has2FA && {
                    password: Yup.string().required(t('FORMS_VALIDATION_REQUIRED'))
                })
            });
        }

        return Yup.object().shape({
            identity: getIdentityValidationScheme(identity, selectedCountry as CountryCode, t),
            ...(!has2FA && {
                password: Yup.string().required(t('FORMS_VALIDATION_REQUIRED'))
            })
        });
    }, [t, identity, selectedCountry, has2FA, useSeparateInputs, activeTab]);

    const handleSignInSuccess = React.useCallback(
        (res: UserData) => {
            dispatch(resetRewards());
            if (onSuccess) {
                onSuccess(res);
                return res;
            }
            const returnUrl = getOnCloseRedirectUrl(merchantId ?? '');
            if (returnUrl) {
                const [path, search] = returnUrl.split('?');
                push(path, {}, search);
            } else {
                push(ROUTES.BASE);
            }

            return res;
        },
        [dispatch, merchantId, onSuccess, push]
    );

    const handleSignInError = React.useCallback(
        (error: AxiosResponse<{ message: string; code: string }>) => {
            if (error.data?.code === 'E-WAF-0001') {
                return setErrorMessage(error.data.message);
            }
            switch (error.status) {
                case 401:
                    setErrorMessage("Sorry! We couldn't find you with that email and password combo.");
                    break;
                default:
                    setErrorMessage(t('GENERAL_ERROR_PROCESSING_REQUEST'));
                    break;
            }
        },
        [t]
    );

    const handleSubmit = React.useCallback(
        (data: LoginFormData) => {
            logUserEvent(EGAEventName.LoginSubmit);
            let identityValue: string;
            let isPhoneInput: boolean;
            if (useSeparateInputs) {
                identityValue = activeTab === 'email' ? data.email : data.phone;
                isPhoneInput = activeTab === 'phone';
            } else {
                identityValue = data.identity;
                isPhoneInput = identity === 'phone';
            }
            if (isPhoneInput) {
                identityValue = formatPhoneAsE164(identityValue, selectedCountry as CountryCode);
            }
            if (has2FA) {
                if (onSwitchToTwoFactor) {
                    onSwitchToTwoFactor(identityValue);
                } else {
                    push(getParsedPath(ROUTES.USER.TWO_FACTOR_AUTHENTICATION), undefined, undefined, {
                        id: identityValue
                    });
                }
            } else {
                const credentials = btoa(`${identityValue}:${data.password}`);

                return dispatch(signIn({ credentials }))
                    .unwrap()
                    .then(handleSignInSuccess)
                    .catch(handleSignInError);
            }
        },
        [
            logUserEvent,
            useSeparateInputs,
            has2FA,
            activeTab,
            identity,
            selectedCountry,
            onSwitchToTwoFactor,
            push,
            getParsedPath,
            dispatch,
            handleSignInSuccess,
            handleSignInError
        ]
    );

    const handleKeyPress = React.useCallback(
        (submit: () => Promise<void>) => (e: React.KeyboardEvent) => {
            if (e.keyCode === 13) {
                submit();
            }
        },
        []
    );

    const handleForgotLinkClick = React.useCallback(
        (e: React.MouseEvent) => {
            logUserEvent(EGAEventName.ForgotPasswordClick);
            if (onSwitchToForgotPassword) {
                e.preventDefault();
                onSwitchToForgotPassword();
            }
        },
        [logUserEvent, onSwitchToForgotPassword]
    );

    const handleSignUpLinkClick = React.useCallback(
        (e: React.MouseEvent) => {
            if (onSwitchToSignUp) {
                e.preventDefault();
                onSwitchToSignUp();
            }
        },
        [onSwitchToSignUp]
    );

    return (
        <Formik
            initialValues={initialFields}
            validationSchema={signinValidation}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({
                submitForm,
                setFieldValue,
                isSubmitting,
                isValid,
                values: { identity: identityField, email, phone, password }
            }) => {
                const countryPicker = (
                    <CountryCodePicker
                        selectedCountry={selectedCountry}
                        onCountryChange={handleCountryChange(setFieldValue)}
                        supportedCodes={settings?.phoneAuth?.supportedCountryCallingCodes ?? []}
                    />
                );
                const isActiveTabFieldEmpty = activeTab === 'email' ? !email : !phone;
                const isIdentityEmpty = useSeparateInputs ? isActiveTabFieldEmpty : !identityField;

                return (
                    <Form>
                        <StyledGrid container spacing={2} noPadding={noPadding}>
                            {!hideSignUpLink && (
                                <Grid size={12}>
                                    <Typography align="center">
                                        {t('AUTH_NO_ACCOUNT') + ' '}
                                        <Link
                                            to={getParsedPath(ROUTES.USER.REGISTER)}
                                            onClick={handleSignUpLinkClick}
                                        >
                                            {t('ONBOARDING_SIGN_UP')}
                                        </Link>
                                    </Typography>
                                </Grid>
                            )}
                            {(settings?.googleSSOEnabled || settings?.appleSSOEnabled) && (
                                <Grid size={12}>
                                    {/* Wrapping row: SSO buttons sit side-by-side when the form is
                                        wide enough for both at their minimum width, and stack
                                        otherwise (mobile, dialogs, the inline guest-conversion column). */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <GoogleSSO
                                            onSignInError={handleSignInError}
                                            onSignInSuccess={handleSignInSuccess}
                                        />
                                        <AppleSSO
                                            onSignInError={handleSignInError}
                                            onSignInSuccess={handleSignInSuccess}
                                        />
                                    </Box>
                                </Grid>
                            )}
                            {(settings?.appleSSOEnabled || settings?.googleSSOEnabled) && (
                                <Grid size={12}>
                                    <Box className={classes.orContainer}>
                                        <Divider />
                                        <Typography className={classes.or}>OR</Typography>
                                    </Box>
                                </Grid>
                            )}
                            <IdentitySelector
                                useSeparateInputs={!!useSeparateInputs}
                                identity={identity}
                                activeTab={activeTab}
                                onTabChange={handleTabChange}
                                countryPicker={countryPicker}
                                showCountryInfo={!!showCountryInfo}
                                size={compact && !has2FA ? { xs: 12, sm: 6 } : 12}
                            />
                            {!has2FA && (
                                <Grid size={compact ? { xs: 12, sm: 6 } : 12}>
                                    <PasswordFormField
                                        name="password"
                                        label={t('ONBOARDING_SIGNIN_PASSWORD')}
                                        onKeyDown={handleKeyPress(submitForm)}
                                    />
                                    <Typography align="right" variant="caption" className={classes.forgot}>
                                        <Link
                                            to={getParsedPath(ROUTES.USER.FORGOT_PASSWORD)}
                                            onClick={handleForgotLinkClick}
                                        >
                                            {t('BUTTON_FORGOTTEN')}
                                        </Link>
                                    </Typography>
                                </Grid>
                            )}
                            {has2FA && (
                                <Grid size={12}>
                                    <Typography
                                        align="right"
                                        variant="caption"
                                        className={classes.forgotWith2FA}
                                    >
                                        <Link
                                            to={getParsedPath(ROUTES.USER.FORGOT_PASSWORD)}
                                            onClick={handleForgotLinkClick}
                                        >
                                            {t('BUTTON_FORGOTTEN')}
                                        </Link>
                                    </Typography>
                                </Grid>
                            )}
                            {!!errorMessage && (
                                <Grid size={12}>
                                    <Typography color="error">{errorMessage}</Typography>
                                </Grid>
                            )}
                            <Grid size={12}>
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    disabled={
                                        !isValid || isAuthLoading || isIdentityEmpty || (!has2FA && !password)
                                    }
                                    fullWidth
                                    onClick={submitForm}
                                    className={classes.button}
                                    loading={isSubmitting || isAuthLoading}
                                >
                                    {t('LOGIN_SUBMIT')}
                                </LoadingButton>
                            </Grid>
                            {!hideTermsAndConditions && (
                                <Grid size={12}>
                                    <TermsAndConditions variant="caption" />
                                </Grid>
                            )}
                        </StyledGrid>
                    </Form>
                );
            }}
        </Formik>
    );
};
